import type { ReactNode } from 'react'
import { VisuallyHidden } from '@n26/nxd'
import type { TrendDirection } from '@/features/dashboard/api/schemas'
import {
  formatChangeIn,
  toldByArrow,
  type ChangeUnit,
  type ValueOptions,
} from '@/features/dashboard/format/format'
import { Odometer, zeroFrom } from '../charts/Odometer'
import styles from './DeltaBadge.module.css'

const TRIANGLE_UP =
  'M17.8892 15.7522C18.2722 16.4188 17.7909 17.2503 17.0221 17.2503H6.97756C6.20872 17.2503 5.72748 16.4188 6.11045 15.7522L11.1327 7.00969C11.5171 6.34052 12.4825 6.34052 12.8669 7.00969L17.8892 15.7522Z'
const TRIANGLE_DOWN =
  'M17.8892 8.24812C18.2722 7.58146 17.7909 6.75 17.0221 6.75H6.97756C6.20872 6.75 5.72748 7.58146 6.11045 8.24813L11.1327 16.9906C11.5171 17.6598 12.4825 17.6598 12.8669 16.9906L17.8892 8.24812Z'

type DeltaBadgeProps = {
  /** A fraction when `unit` is relative (0.059 for +5.9%), else the difference in ppt / pts or the card's unit. */
  change: number
  /** How the change reads: relative shows '5.9%', ppt / pts a signed difference. */
  unit?: ChangeUnit
  /** The card's unit and format, which an absolute difference is written in. */
  value?: ValueOptions
  direction: TrendDirection
  /** A fall is good news: green and red swap, the arrow doesn't. */
  lowerIsBetter?: boolean
  /** small: a tile's 16px variation; large: the expanded card's 20px one; row: a status row's 14px one. */
  size?: 'small' | 'large' | 'row'
  /** Shown after the change, in its colour (a status row's difference in euros). */
  children?: ReactNode
}

// The change rolls up from zero.
export const DeltaBadge = ({
  change: amount,
  unit = 'relative',
  value,
  direction,
  lowerIsBetter = false,
  size = 'small',
  children,
}: DeltaBadgeProps) => {
  // A tile spells out the sign next to its arrow; the larger badges let the
  // arrow tell a relative change, and a difference keeps its sign.
  const text = formatChangeIn(amount, unit, value)
  const change =
    size !== 'small' && toldByArrow(unit) ? text.replace(/^[+−]/, '') : text
  return (
    <span
      className={`${styles.badge} ${styles[size]} ${styles[direction]} ${lowerIsBetter ? styles.inverted : ''}`}
    >
      {direction !== 'flat' && (
        <svg viewBox='0 0 24 24' width='24' height='24' aria-hidden='true'>
          <path
            fill='currentColor'
            d={direction === 'up' ? TRIANGLE_UP : TRIANGLE_DOWN}
          />
        </svg>
      )}
      {direction !== 'flat' && (
        <VisuallyHidden>{direction === 'up' ? 'Up' : 'Down'}</VisuallyHidden>
      )}
      <VisuallyHidden>{change}</VisuallyHidden>
      <Odometer
        value={change}
        from={zeroFrom(change)}
        direction={direction === 'down' ? 'down' : 'up'}
        inverted={lowerIsBetter}
      />
      {children}
    </span>
  )
}
